import { createModal } from './modal.js';
import { openAddressForm } from './address-form.js';
import { formatAddress, formatAddressOneLine } from './address-format.js';
import { dummyAll, post } from './persist.js';
import { fetchWithRedirectGuard } from './http.js';

// Address book is now the front door for both the single delivery-address
// flow and the add/edit-recipient flow — the checkout button or recipient
// card opens THIS dialog directly (see modal.js's dialog.__opener), and the
// address-editor form dialog is only ever opened as a child of this one
// (Add New Address, or the per-row Edit button). Billing never touches this
// dialog at all.

// Label shown in the sort button after "Sort by:" for each mode. The mode
// keys match the `data-value` attribute on each <li class="abook-sort__option">
// in the rendered Pug — keep them in sync.
const SORT_LABELS = {
  firstNameAsc:  'A-Z by First Name',
  firstNameDesc: 'Z-A by First Name',
  lastNameAsc:   'A-Z by Last Name',
  lastNameDesc:  'Z-A by Last Name',
};

const DEFAULT_SORT = 'lastNameAsc';

function cmpStr(a, b) {
  return (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' });
}

function sortAddresses(arr, mode) {
  const sorted = [...arr];
  switch (mode) {
    case 'firstNameAsc':  sorted.sort((a, b) => cmpStr(a.firstName, b.firstName)); break;
    case 'firstNameDesc': sorted.sort((a, b) => cmpStr(b.firstName, a.firstName)); break;
    case 'lastNameDesc':  sorted.sort((a, b) => cmpStr(b.lastName,  a.lastName));  break;
    case 'lastNameAsc':
    default:              sorted.sort((a, b) => cmpStr(a.lastName,  b.lastName));  break;
  }
  return sorted;
}

export function initAddressBook() {
  document.querySelectorAll('[data-address-book]').forEach(wrap => createInstance(wrap));
}

function createInstance(wrap) {
  const dialog    = wrap.querySelector('.abook-modal');
  if (!dialog) return null;

  const backBtn   = dialog.querySelector('.modal__back');
  const emptyEl   = dialog.querySelector('.abook-empty');
  const listEl    = dialog.querySelector('.abook-list');
  const addNewBtn = dialog.querySelector('.abook-add-btn');
  const selectBtn = dialog.querySelector('.modal__confirm');
  const errorEl   = dialog.querySelector('.modal__error');

  const sortEl       = dialog.querySelector('.abook-sort');
  const sortBtn      = sortEl?.querySelector('.abook-sort-btn');
  const sortValueEl  = sortBtn?.querySelector('.abook-sort-btn__value');
  const sortListEl   = sortEl?.querySelector('.abook-sort__list');
  const sortOptions  = sortListEl ? Array.from(sortListEl.querySelectorAll('.abook-sort__option')) : [];

  const loadUrl      = wrap.dataset.loadUrl || null;
  // Canonical create/update endpoint for the address editor opened from
  // this book (Add New Address, or a row's Edit button) — distinct from
  // each opener's own data-save-url, which is the "attach this address to
  // my context" endpoint used only for the single-select finalize step.
  const editorSaveUrl = wrap.dataset.addressSaveUrl || null;
  const editorDialogId = wrap.dataset.editorDialog || 'address-editor';

  if (addNewBtn) addNewBtn.dataset.saveUrl = editorSaveUrl || '';

  let originalOpener  = null;
  let mode            = 'single';
  let remaining       = Infinity;
  let addresses       = [];
  let selectedIds     = new Set();
  let sortMode        = DEFAULT_SORT;

  function clearError() {
    if (!errorEl) return;
    errorEl.textContent = '';
    errorEl.hidden = true;
  }

  function showError(msg) {
    if (!errorEl) return;
    errorEl.textContent = msg;
    errorEl.hidden = false;
  }

  function atCapacity() {
    return mode === 'multi' && selectedIds.size >= remaining;
  }

  function upsertAddress(addr) {
    const idx = addresses.findIndex(a => String(a.id) === String(addr.id));
    if (idx >= 0) addresses[idx] = { ...addresses[idx], ...addr };
    else addresses.push(addr);
    addresses = sortAddresses(addresses, sortMode);
  }

  function render() {
    if (emptyEl) emptyEl.hidden = addresses.length > 0;

    listEl.replaceChildren();
    addresses.forEach(addr => {
      const li = document.createElement('li');
      li.className = 'abook-option';

      const input = document.createElement('input');
      input.value = addr.id;
      input.checked = selectedIds.has(addr.id);

      if (mode === 'multi') {
        input.type = 'checkbox';
        input.name = 'abook-selection';
        // Lock out further picks once the user has selected as many
        // addresses as there are remaining quantities to allocate.
        input.disabled = !input.checked && atCapacity();
        input.addEventListener('change', () => {
          if (input.checked) selectedIds.add(addr.id);
          else               selectedIds.delete(addr.id);
          clearError();
          render();
        });
      } else {
        input.type = 'radio';
        input.name = 'abook-selection';
        input.addEventListener('change', () => {
          selectedIds.clear();
          selectedIds.add(addr.id);
          clearError();
        });
      }

      const text = document.createElement('p');
      text.className = 'abook-option__text';

      const nameEl = document.createElement('span');
      nameEl.className = 'abook-option__name';
      nameEl.textContent = [addr.firstName, addr.lastName].filter(Boolean).join(' ');

      const addrEl = document.createElement('span');
      addrEl.className = 'abook-option__address';
      addrEl.textContent = [addr.addressLine1, addr.addressLine2, addr.addressLine3, addr.city, addr.postcode]
        .filter(s => s && s.trim())
        .join(', ');

      text.append(nameEl, addrEl);

      li.append(input, text);
      if (input.disabled) li.classList.add('is-disabled');
      li.addEventListener('click', e => {
        if (input.disabled) return;
        if (e.target === input) return;
        input.click();
      });
      listEl.appendChild(li);
    });
  }

  // Scroll a specific address's row into view — used after add/edit so the
  // customer can see the one they just confirmed, now shown selected.
  function scrollToAddress(id) {
    listEl.querySelector(`input[value="${CSS.escape(String(id))}"]`)
      ?.closest('.abook-option')
      ?.scrollIntoView({ block: 'nearest' });
  }

  async function loadAddresses() {
    // Pre-integration: read from persist.js's dummy store. Any address
    // saved via the editor during this session lands there too, so it
    // shows up in the picker without a separate "refresh" step — the
    // same behaviour the Spring backend will give us via a server-side
    // address-book endpoint.
    if (!loadUrl) return dummyAll();
    try {
      const res = await fetchWithRedirectGuard(loadUrl);
      if (!res.ok) throw new Error(`Address book load failed (${res.status})`);
      return await res.json();
    } catch (err) {
      console.error(err);
      return [];
    }
  }

  // Open the address editor blank, to add a brand new address to the book.
  function openEditorForNew() {
    delete addNewBtn?.dataset.savedId;
    openAddressForm(editorDialogId, null, addNewBtn);
  }

  addNewBtn?.addEventListener('click', () => openEditorForNew());

  // Catches address-form:confirm bubbling up from the editor's activeOpener
  // — addNewBtn (Add New Address), the only opener left that lives inside
  // this dialog now that a book row's own Edit button is gone (editing a
  // recipient's address goes straight to the editor from the recipients
  // popup instead — see recipients.js) — so the event bubbles here naturally.
  // There's no separate "highlight" state: the address just added/edited
  // becomes the (or an) active selection, and the existing selected-border
  // style is the only visual pointer to it.
  dialog.addEventListener('address-form:confirm', e => {
    const addr = e.detail;
    const isAddNew = e.target === addNewBtn;

    upsertAddress(addr);
    if (mode === 'single') selectedIds = new Set([addr.id]);
    else                    selectedIds.add(addr.id);

    render();
    scrollToAddress(addr.id);

    if (isAddNew && mode === 'single') {
      finalizeSingleSelection(addr);
    } else if (isAddNew && mode === 'multi') {
      // Add Recipient: a brand-new address always finalizes immediately as
      // a recipient and closes the book, rather than leaving the book open
      // with it merely selected — regardless of whether the book had other
      // (unselected) addresses to offer.
      finalizeMultiSelection([addr]);
    }
  });

  // The editor closed (Confirm or cancel) — if it was the empty-book
  // bootstrap (primeForOpener's addresses.length === 0 branch below) and
  // nothing got added, this book is still just as pointless as when it
  // auto-opened the editor in the first place: close it too, rather than
  // stranding the customer on an empty list. A successful add/edit always
  // pushes into `addresses` (above) before the editor closes, so this only
  // ever fires true on a genuine cancel.
  const editorDialog = document.getElementById(editorDialogId);
  editorDialog?.addEventListener('close', () => {
    if (addresses.length === 0) modalInstance.close();
  });

  async function primeForOpener(opener) {
    originalOpener = opener || null;
    const remainingAttr = originalOpener?.dataset.remaining;
    mode      = remainingAttr ? 'multi' : 'single';
    remaining = mode === 'multi' ? (parseInt(remainingAttr, 10) || 0) : Infinity;
    selectedIds = new Set();

    // Select is always enabled — clicking it with nothing chosen surfaces
    // a "Select an address" error rather than being disabled. Defensively
    // strip a `disabled` attribute the server template may have inherited
    // from the earlier shape of this dialog.
    if (selectBtn) selectBtn.disabled = false;

    clearError();
    const loaded = await loadAddresses();
    let list = sortAddresses(loaded, sortMode);

    // Add Recipient (multi mode) excludes addresses already used by a
    // recipient — recipients.js stamps their addressIds onto the opener as
    // JSON before triggering this open. Single-select (delivery address)
    // never sets this, so the filter is a no-op there.
    if (mode === 'multi') {
      let excludeIds = [];
      try { excludeIds = JSON.parse(originalOpener?.dataset.excludeIds || '[]'); } catch { excludeIds = []; }
      if (excludeIds.length) {
        const excludeSet = new Set(excludeIds.map(String));
        list = list.filter(a => !excludeSet.has(String(a.id)));
      }
    }
    addresses = list;

    // Nothing to pick from — no saved addresses at all, or (multi mode)
    // every one is already used by a recipient — skips straight to the
    // editor, same experience as a new customer with an empty book.
    if (addresses.length === 0) {
      render();
      openEditorForNew();
      return;
    }

    if (mode === 'single' && originalOpener?.dataset.savedId) {
      const preselect = addresses.find(a => String(a.id) === String(originalOpener.dataset.savedId));
      if (preselect) selectedIds.add(preselect.id);
    }

    render();
  }

  // Sort dropdown wiring. Mirrors the country-select pattern: button
  // toggles a listbox, click an option to set the sort, outside-click +
  // Escape close. Re-sort + re-render on every selection.
  function openSort()   { if (sortListEl) { sortListEl.hidden = false; sortBtn?.setAttribute('aria-expanded', 'true'); } }
  function closeSort()  { if (sortListEl) { sortListEl.hidden = true;  sortBtn?.setAttribute('aria-expanded', 'false'); } }
  function toggleSort() { sortListEl?.hidden ? openSort() : closeSort(); }

  function setSortMode(nextMode) {
    sortMode = SORT_LABELS[nextMode] ? nextMode : DEFAULT_SORT;
    if (sortValueEl) sortValueEl.textContent = SORT_LABELS[sortMode];
    sortOptions.forEach(o => o.setAttribute('aria-selected', String(o.dataset.value === sortMode)));
    addresses = sortAddresses(addresses, sortMode);
    render();
  }

  if (sortBtn && sortListEl) {
    sortBtn.addEventListener('click', toggleSort);
    sortBtn.addEventListener('keydown', e => {
      if (e.key === 'Escape') closeSort();
    });
    sortOptions.forEach(opt => {
      opt.addEventListener('mousedown', e => e.preventDefault());
      opt.addEventListener('click', () => {
        setSortMode(opt.dataset.value);
        closeSort();
        sortBtn.focus();
      });
    });
    document.addEventListener('click', e => {
      if (sortListEl.hidden) return;
      if (!sortEl.contains(e.target)) closeSort();
    });
  }

  const modalInstance = createModal(dialog, {
    closeOnBackdrop: false,
    onBeforeOpen: primeForOpener,
    onClose: () => {
      originalOpener = null;
      selectedIds     = new Set();
    },
  });

  backBtn?.addEventListener('click',  () => modalInstance.close());

  // Single-select finalize: attach the picked/added address to whatever
  // opened the book (the single delivery-address flow — the only single-
  // select consumer now that editing a recipient's address goes straight
  // to the editor, bypassing the book entirely) and close.
  async function finalizeSingleSelection(address) {
    const saveUrl = originalOpener?.dataset.saveUrl;
    let saved = address;

    if (saveUrl) {
      clearError();
      try {
        const response = await post(saveUrl, { id: address.id });
        saved = { ...address, ...(response || {}) };
      } catch (err) {
        console.error(err);
        showError('Could not save address. Please try again.');
        return;
      }
    }

    modalInstance.close();

    if (originalOpener) {
      originalOpener.dataset.savedId = saved.id || '';
      originalOpener.dispatchEvent(new CustomEvent('address-form:confirm', {
        bubbles: true,
        detail: {
          ...saved,
          formatted:        formatAddress(saved),
          formattedOneLine: formatAddressOneLine(saved),
        },
      }));
    }
  }

  selectBtn?.addEventListener('click', () => {
    if (selectedIds.size === 0) {
      showError(mode === 'multi' ? 'Select at least one address' : 'Select an address');
      return;
    }

    if (mode === 'multi') {
      finalizeMultiSelection(addresses.filter(a => selectedIds.has(a.id)));
      return;
    }

    const pickedId = [...selectedIds][0];
    const picked   = addresses.find(a => a.id === pickedId);
    if (!picked) return;
    finalizeSingleSelection(picked);
  });

  // Multi-select finalize: hand the picked addresses to whatever opened the
  // book (always the "Add Recipient" flow) and close. No attach POST here —
  // the recipient → address link is saved later via the recipients dialog's
  // own Confirm, same as the direct-Select path this also serves.
  function finalizeMultiSelection(picked) {
    modalInstance.close();
    originalOpener?.dispatchEvent(new CustomEvent('address-book:multi-confirm', {
      bubbles: true,
      detail: { addresses: picked },
    }));
  }
}
